import { dehydrate, QueryClient } from '@tanstack/react-query';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { Router } from 'shared-routing';
import { Property } from 'shared-types/Property';

import { Packages } from '@features/packages/pages/Packages';
import { getModuleStatuses, propertiesKeys } from '@features/properties';
import {
  getAvailableRates,
  getRatePageSettings,
  getStaticRates,
  RatesFilterType,
  ratesKeys,
} from '@features/rates';
import { getSpaStaysPagePrefetch } from '@features/spa/hooks';
import { roomsKeys } from '@features/rooms/queries';
import { getRoomPageSettings } from '@features/rooms/requests';
import { PagePropsWithSSR } from '@features/state';
import { useTranslations } from '@lang';
import { useLabels } from '@features/Menu/useLabels';
import { NormalizedStore } from '@store/entity/entity.types';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';

type SpaStaysPageProps = {
  language: string;
  url: string;
  property: Property;
};

export const SpaStaysPage: NextPage<SpaStaysPageProps> = ({ language, url, property }) => {
  const { t } = useTranslations();
  const labels = useLabels();

  const pageName = labels?.spastays || t('flynav.spaStays');

  const pageTitle = property.propertyTitle ? `${pageName} | ${property.propertyTitle}` : pageName;
  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: property.socialFallbackImage?.url || '' }],
          },
        }}
      />
      <Packages property={property} language={language} type={RatesFilterType.SPA_STAYS} />
    </>
  );
};

type SpaStaysPageInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<SpaStaysPageProps, 'language' | 'url' | 'property'>;

SpaStaysPage.getInitialProps = async (
  props: SpaStaysPageInitialProps
): Promise<PagePropsWithSSR<SpaStaysPageProps>> => {
  const { store, property, language, url, res, query } = props;
  const { id: siteId, slug: siteSlug } = property;

  if (!property.modulesExcludingDisabled?.spa?.features?.stays?.enabled) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];
  const promoRateCode = typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined;

  const client = new QueryClient();
  await client.prefetchQuery({
    queryKey: ratesKeys.staticRates({ siteSlug, promoRateCode, language }),
    queryFn: () => getStaticRates({ siteSlug, promoRateCode, language }),
  });
  await client.prefetchQuery({
    queryKey: ratesKeys.availableRates({
      siteId,
      fromDate,
      toDate,
      adults,
      children,
      childAges,
      promoRateCode,
      rooms,
      isMultiRoomsEnabled: rooms.length > 1,
      type: RatesFilterType.SPA_STAYS,
    }),
    queryFn: () =>
      getAvailableRates({
        siteId,
        fromDate,
        toDate,
        adults,
        children,
        childAges,
        promoRateCode,
        rooms,
        isMultiRoomsEnabled: rooms.length > 1,
        type: RatesFilterType.SPA_STAYS,
      }),
  });
  await client.prefetchQuery({
    queryKey: propertiesKeys.moduleStatuses({ siteSlug }),
    queryFn: () => getModuleStatuses({ siteSlug }),
  });
  await client.prefetchQuery({
    queryKey: roomsKeys.roomSettings({ siteId, language }),
    queryFn: () => getRoomPageSettings({ siteId, language }),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  await client.prefetchQuery({
    queryKey: ratesKeys.rateSettings({ siteId, language }),
    queryFn: () => getRatePageSettings({ siteId, language }),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  await getSpaStaysPagePrefetch(client, siteId, language);

  return {
    property,
    language,
    url,
    dehydratedState: dehydrate(client),
  };
};

export default SpaStaysPage;
